import { createContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { User, AppId } from '@homeapp/shared';

interface AuthState {
  user: (User & { access: AppId[] }) | null;
  token: string | null;
  loading: boolean;
  mustChangePassword: boolean;
}

interface AuthContextType extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  setPasswordChanged: () => void;
  updateUser: (user: User & { access: AppId[] }) => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

const API_BASE = '/api';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    loading: true,
    mustChangePassword: false,
  });

  const fetchMe = useCallback(async (token: string) => {
    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Unauthorized');
      const data = await res.json();
      setState({
        user: data.user,
        token,
        loading: false,
        mustChangePassword: data.user.mustChangePassword,
      });
    } catch {
      localStorage.removeItem('homeapp_token');
      setState({ user: null, token: null, loading: false, mustChangePassword: false });
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('homeapp_token');
    if (token) {
      fetchMe(token);
    } else {
      setState(s => ({ ...s, loading: false }));
    }
  }, [fetchMe]);

  const login = async (username: string, password: string) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Login failed' }));
      throw new Error(err.message || 'Login failed');
    }
    const data = await res.json();
    localStorage.setItem('homeapp_token', data.token);
    setState({
      user: { ...data.user, access: data.user.access || [] },
      token: data.token,
      loading: false,
      mustChangePassword: data.mustChangePassword,
    });
  };

  const logout = () => {
    localStorage.removeItem('homeapp_token');
    setState({ user: null, token: null, loading: false, mustChangePassword: false });
  };

  const setPasswordChanged = () => {
    setState(s => ({ ...s, mustChangePassword: false }));
  };

  const updateUser = (user: User & { access: AppId[] }) => {
    setState(s => ({ ...s, user }));
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout, setPasswordChanged, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}
